import type { ReactNode } from 'react'
import React from 'react'
import Link from 'next/link'
import type { LinkToArticlePageFragment } from '../../../.graphql/generated-types'

/**
 * Build a href that points to a legacy article
 *
 * This is needed because we want users to navigate to legacy articles located
 * on the legacy host, rather than articles located on Vercel.
 *
 * For example:
 * ```
 * buildHrefToLegacyArticle('2022-01-01-hello-world')
 *
 * // Will output:
 * '/articles/2022/01/01/hello-world'
 * ```
 */
function buildHrefToLegacyArticle(articleSlugFromContentful: string): string {
  let articleSlug = articleSlugFromContentful
  const patternToTransform = /(^\d{4}-\d{2}-\d{2}-)/
  const maybeAMatch = patternToTransform.exec(articleSlugFromContentful)

  if (Array.isArray(maybeAMatch) && typeof maybeAMatch[0] === 'string') {
    const articleSlugPartWithOnlyHyphens = maybeAMatch[0]

    const legacyArticleSlugPartWithSlashes =
      articleSlugPartWithOnlyHyphens.replace(/-/g, '/')

    articleSlug = articleSlugFromContentful.replace(
      articleSlugPartWithOnlyHyphens,
      legacyArticleSlugPartWithSlashes,
    )
  }
  return `/articles/${articleSlug}`
}

export interface LinkToArticlePageProps {
  article: LinkToArticlePageFragment
  className?: string
  children: ReactNode
}

/**
 * Link to article page component
 *
 * This component builds a link to an article page using `next/link` or a normal anchor element.
 *
 * Why do we need this component?
 *
 * Because content editors want to control whether or not users should navigate to the legacy article
 * or the one hosted on Vercel. And since `next/link` always attempts to navigate to an article in Vercel due to the
 * fact a file exists at /app/[product]/src/pages/articles/[slug].tsx.
 *
 */
export function LinkToArticlePage({
  article,
  className,
  children,
}: LinkToArticlePageProps) {
  const slug = article.slug ?? ''
  const href = `/articles/${slug}`
  const legacyArticleHref = buildHrefToLegacyArticle(slug)
  if (article.isNextJsRoutable) {
    return (
      <Link href={href} className={className}>
        {children}
      </Link>
    )
  }
  return (
    <a href={legacyArticleHref} className={className}>
      {children}
    </a>
  )
}
